import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Button from '../../../components/bootstrap/Button';
import Logo from '../../../components/Logo';
import useDarkMode from '../../../hooks/useDarkMode';
import apiServicewt from '../../../api/apiServicewt';
import apiService from '../../../api/apiService';
import PhoneNumberInput from '../../../components/common/PhoneNumberInput';
import './Login.css';
import { useLoginSettings } from '../../../contexts/loginSettingsContext';
import { userAuth } from '../../../contexts/userContext';

const GooglePhoneNumber = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const googleUser = location.state?.googleUser;
	const { darkModeStatus } = useDarkMode();
	const { loginSettings, loginSettingsLang } = useLoginSettings();
	const { setToken } = useContext(userAuth);

	const [phoneInputValue, setPhoneInputValue] = useState('');
	const [countryOptions, setCountryOptions] = useState([]);
	const [errorMessage, setErrorMessage] = useState('');
	const [isLoading, setIsLoading] = useState(false);

	const rawDefaultCountryCode =
		loginSettings?.permission_settings?.default_country_code ??
		loginSettings?.permission_settings?.registration?.default_country_code;
	const defaultCountryCode = String(rawDefaultCountryCode || '').trim() || '94';
	const loginBgFromSettings = loginSettings?.background_image;
	const defaultLoginBg = `${process.env.PUBLIC_URL}/assets/img/abstract/Main_a.jpg`;
	const loginBg =
		loginBgFromSettings && String(loginBgFromSettings).trim()
			? loginBgFromSettings
			: defaultLoginBg;

	useEffect(() => {
		document.body.classList.add('auth-page');
		return () => document.body.classList.remove('auth-page');
	}, []);

	useEffect(() => {
		if (!googleUser?.google_token) {
			navigate('/login');
		}
	}, [googleUser, navigate]);

	useEffect(() => {
		const countryList = loginSettingsLang?.country_list;
		const phoneCodes = loginSettingsLang?.country_phone_code;

		if (countryList && typeof countryList === 'object') {
			const options = Object.entries(countryList).map(([code, name]) => ({
				code,
				name,
				phoneCode: `+${phoneCodes?.[code] ?? ''}`,
			}));
			setCountryOptions(options);
		}
	}, [loginSettingsLang]);

	useEffect(() => {
		setPhoneInputValue((prev) => prev || `+${defaultCountryCode}`);
	}, [defaultCountryCode]);

	const fullName = googleUser.name?.trim() || '';
	const nameParts = fullName.split(' ');

	const firstName = nameParts[0] || '';
	const middleName = nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : '';
	const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
	const defaultCountryIso = useMemo(
		() =>
			(
				countryOptions.find((option) => option.phoneCode === `+${defaultCountryCode}`)?.code || ''
			).toLowerCase(),
		[countryOptions, defaultCountryCode],
	);

	if (!googleUser?.google_token) return null;

	const fetchBranches = async (tokenData) => {
		const item = { action: 'get_branches_list' };

		try {
			const flechdata = await apiService(item, tokenData, setToken, navigate);

			if (Array.isArray(flechdata.data) && flechdata.data.length > 0) {
				const temp = flechdata.data.map((ele) => ({
					value: ele.branch_id,
					text: ele.branch_name,
				}));

				const defaultBranch = flechdata.data.find((branch) => branch.is_default);
				localStorage.setItem('branches-list', JSON.stringify(temp));
				localStorage.setItem(
					'selectedBranch',
					defaultBranch ? defaultBranch.branch_id : temp[0].value,
				);
			} else {
				localStorage.removeItem('branches-list');
			}
		} catch (error) {
			localStorage.removeItem('branches-list');
			console.error('Error fetching branches:', error);
		}
	};

	const handleContinue = async () => {
		const derivedDialCode = (() => {
			const match = phoneInputValue?.match(/^\+(\d{1,4})/);
			return match ? match[1] : '';
		})();
		const cleanedPhoneNumber = (() => {
			if (!phoneInputValue) return '';
			if (derivedDialCode) {
				return phoneInputValue.replace(`+${derivedDialCode}`, '').replace(/\D/g, '');
			}
			return phoneInputValue.replace(/\D/g, '');
		})();

		if (!derivedDialCode) {
			setErrorMessage('Phone code is required.');
			return;
		}
		if (!cleanedPhoneNumber) {
			setErrorMessage('Phone Number is required.');
			return;
		}
		// if (cleanedPhoneNumber.length < 10) {
		// 	setErrorMessage('Phone Number must be at least 10 digits.');
		// 	return;
		// }

		setErrorMessage('');
		setIsLoading(true);

		try {
			const item = {
				action: 'login_with_google',
				'first_name': firstName || undefined,
				'middle_name': middleName || undefined,
				'last_name': lastName || undefined,
				email: googleUser.email || undefined,
				'phone_code': derivedDialCode,
				phone: cleanedPhoneNumber,
				'google_token': googleUser.google_token,
			};
			const sanitizedItem = Object.fromEntries(
				Object.entries(item).filter(([, value]) => value !== undefined && value !== null),
			);

			const result = await apiServicewt(sanitizedItem, navigate);
			const responseType = String(result?.type || '').toLowerCase();

			if (
				(responseType === 'success' || result?.success === true) &&
				result?.data?.userrole === 'cus'
			) {
				setToken(result.data);
				localStorage.setItem('user-auth', JSON.stringify(result.data));
				await fetchBranches(result.data);
				navigate('/services');
				return;
			}

			setErrorMessage(result?.message || 'Google login failed. Please try again.');
		} catch (error) {
			setErrorMessage(error?.message || 'Could not complete Google login. Please try again.');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className='bg-img' style={{ backgroundImage: `url(${loginBg})` }}>
			<PageWrapper title='Google Signup'>
				<Page
					className='p-0 container-xxl'
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						minHeight: '100vh',
					}}>
					<Card
						className='shadow-3d-dark'
						style={{
							maxWidth: '400px',
							width: '100%',
						}}>
						<CardBody
							style={{
								justifyContent: 'center',
							}}>
							<div className='text-center my-3'>
								<Link
									to='/'
									className={classNames('text-decoration-none fw-bold display-2', {
										'text-dark': !darkModeStatus,
										'text-light': darkModeStatus,
									})}>
									<Logo width={200} />
								</Link>
							</div>

							<div className='text-center h2 fw-bold mt-3'>Enter Your Phone Number</div>
							<div className='text-center text-muted mt-2 mb-4'>
								Complete your Google signup by adding your phone number.
							</div>

							<div className='mb-3 p-3 rounded border bg-light'>
								<div className='fw-bold'>{googleUser.name || 'Google Account'}</div>
								{googleUser.email ? (
									<div className='text-muted'>{googleUser.email}</div>
								) : null}
							</div>

							<form
								className='row g-4'
								onSubmit={(event) => {
									event.preventDefault();
									handleContinue();
								}}>
								<div className='col-12'>
									<FormGroup id='google-phone' isFloating>
										<PhoneNumberInput
											value={phoneInputValue}
											defaultCountryIso={defaultCountryIso}
											onChange={(value) => {
												setPhoneInputValue(value);
												setErrorMessage('');
											}}
											inputProps={{
												autoComplete: 'tel',
											}}
										/>
									</FormGroup>
									{errorMessage ? (
										<p style={{ color: 'red', fontSize: '12px' }}>{errorMessage}</p>
									) : null}
								</div>

								<div className='col-12'>
									<Button
										type='submit'
										color='success'
										className='w-100 py-3'
										disabled={isLoading}>
										{isLoading ? 'Please wait...' : 'Continue'}
									</Button>
								</div>
							</form>
						</CardBody>
					</Card>
				</Page>
			</PageWrapper>
		</div>
	);
};

export default GooglePhoneNumber;
